import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, useFormState } from 'react-final-form';
import { EntityService } from 'core/api/services/lookUp';
import { Box, FormHelperText, Typography } from '@mui/material';
import { CurrencyInput, LookUpPriceGroup, RadioGroup } from '@utriper/core-ui';
import { Container, Label } from '../../styles';
import { PRICE_TYPE } from 'modules/Experience/ExperienceCalendar/hook';

const PriceTab = ({ form }) => {
    const { t } = useTranslation();
    const translate = (key) => t(`experience_calendar.schedule_form.price_tab.${key}`);
    const { values } = useFormState();

    const priceTypeOptions = useMemo(
        () => [
            {
                id: PRICE_TYPE.FIXED,
                label: (
                    <Box>
                        <Typography component="h4" fontSize={16}>
                            {translate('price_type_options.fixed_label')}
                        </Typography>
                        <Typography fontSize={14}>{translate('price_type_options.fixed_description')}</Typography>
                    </Box>
                ),
            },
            {
                id: PRICE_TYPE.PRICE_GROUP,
                label: (
                    <Box>
                        <Typography component="h4" fontSize={16}>
                            {translate('price_type_options.price_group_label')}
                        </Typography>
                        <Typography fontSize={14}>{translate('price_type_options.price_group_description')}</Typography>
                        {!values?.hasPriceGroup && <FormHelperText sx={{ margin: 0, color: 'inherit' }}>{translate('price_type_options.disabled_price_group')}</FormHelperText>}
                    </Box>
                ),
                disabled: !values?.hasPriceGroup,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [values?.hasPriceGroup]
    );

    const getList = async ({ entity, search, input }) => EntityService.getListByExperienceId({ entity, search, input, experienceId: values?.experienceId });

    return (
        <React.Fragment>
            {/* Radio Group */}
            <Container
                sx={{
                    '& .MuiFormControlLabel-root': {
                        display: 'flex',
                        alignItems: 'flex-start',
                    },
                }}
            >
                <Label>{translate('price_type_label')}</Label>
                <Field id="priceType" name="priceType" component={RadioGroup} options={priceTypeOptions} />
            </Container>

            {values?.priceType === PRICE_TYPE.FIXED && (
                <Container>
                    <Label>{translate('price_label')}</Label>
                    <Field
                        id="price"
                        name="price"
                        label={''}
                        currency={values?.currency}
                        onCurrencyChange={(newCurrency) => {
                            form.change('currency', newCurrency);
                        }}
                        render={({ input, meta, ...rest }) => (
                            <CurrencyInput
                                {...rest}
                                value={typeof input?.value === 'number' ? input.value : 0}
                                onChange={(newValue) => {
                                    input.onChange(newValue);
                                }}
                            />
                        )}
                    />
                </Container>
            )}

            {values?.priceType === PRICE_TYPE.PRICE_GROUP && (
                <Container
                    sx={{
                        '& .MuiAutocomplete-root': {
                            width: '100%',
                        },
                    }}
                >
                    <Label>{translate('price_group_label')}</Label>
                    <Field
                        id="priceGroupId"
                        name="priceGroupId"
                        render={({ input }) => (
                            <LookUpPriceGroup
                                {...input}
                                label={translate('price_group_placeholder')}
                                entityService={{ getList }}
                                value={values?.priceGroupId}
                                initialValue={values?.priceGroupId}
                                onChange={(newValue) => {
                                    form.change('priceGroupId', newValue);
                                }}
                                disabled={!values?.hasPriceGroup}
                            />
                        )}
                    />
                </Container>
            )}
        </React.Fragment>
    );
};

export default PriceTab;
